import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getProductList } from 'src/api/product';
import { BASE_URL } from 'src/config/api';
import { initialState, ProductSearchInitType } from './state';
import { AppState } from 'src/store';
import { ProductOld } from 'src/admin-entity/interface/product/product-old.interface';
import { dateFormatter } from 'src/utils/dateFormatter';
import * as api from 'src/api/category/category';
import { SimpleDateSelectType } from 'src/admin-entity/enum/search/simple-date-select-type.enum';
import { getStartEndDate } from 'src/utils/getStartEndDate';

type filterType = 'lastest' | 'priceOrderAsc' | 'priceOrderDesc' | 'updateDesc';
type taxStatusType = 'tax-all' | 'taxable' | 'excempt' | 'small';

/**
 * page, take 다 value에서 가져올 수 있으나.. 이미 다른 여러곳에서 입력받게 해놓는 바람에 이렇게 되었음.
 * 추후에 수정해야함.
 */
function getSearchUrl(
    value: ProductSearchInitType,
    page: number,
    take: number,
    isProductList?: boolean,
    withReviewCount?: boolean,
): URL {
    const {
        productType,
        category,
        distributorName,
        productName,
        keyword,
        saleStatus,
        saleType,
        isVideo,
        startDate,
        endDate,
        taxStatus,
        startUpdateDate,
        endUpdateDate,
        filter,
        selectedStartDateType,
        selectedUpdateDateType,
        useGift,
        shipmentDateOptionType,
        isSpecialSale,
        productOptionName,
    } = value;
    const url = new URL(`${BASE_URL}/v1/admin/products/search`);
    url.searchParams.append('page', `${page}`);
    url.searchParams.append('take', `${take}`);
    if (productType !== 0) url.searchParams.append(`productType`, `${productType}`);
    if (category.id !== 0) url.searchParams.append(`categoryId`, `${category.id}`);
    if (distributorName) url.searchParams.append(`distributorName`, distributorName);
    if (productName) url.searchParams.append('productName', productName);
    if (keyword) url.searchParams.append('keyword', keyword);
    if (saleStatus && saleStatus.length > 0) url.searchParams.append('productStatus', saleStatus.join('|'));
    if (isVideo && isVideo !== 'all-video') url.searchParams.append('isVideo', isVideo);
    if (saleType && saleType !== 'all') url.searchParams.append(`saleType`, saleType);
    if (useGift !== 'gift-all') url.searchParams.append('useGift', useGift);
    if (shipmentDateOptionType !== 'all')
        url.searchParams.append(`shipmentDateOptionType`, `${shipmentDateOptionType}`);
    if (isSpecialSale !== 'all') url.searchParams.append(`isSpecialSale`, `${isSpecialSale}`);
    if (productOptionName) url.searchParams.append(`productOptionName`, productOptionName);
    if (filter) url.searchParams.set('filter', filter);
    if (withReviewCount) url.searchParams.append('withReviewCount', 'true');

    if (isProductList) {
        url.searchParams.set('filter', filter);

        if (selectedStartDateType !== 'allDate') {
            url.searchParams.set('startDate', dateFormatter(new Date(startDate), 'yyyy-MM-DD'));
            url.searchParams.set('endDate', dateFormatter(new Date(endDate), 'yyyy-MM-DD'));
        }
        if (selectedUpdateDateType !== 'allDate') {
            url.searchParams.set('startUpdateDate', dateFormatter(new Date(startUpdateDate), 'yyyy-MM-DD'));
            url.searchParams.set('endUpdateDate', dateFormatter(new Date(endUpdateDate), 'yyyy-MM-DD'));
        }
        if (taxStatus && taxStatus !== 'tax-all') url.searchParams.append('taxStatus', taxStatus);
    }

    return url;
}

export const searchProduct = createAsyncThunk(
    `productSearch/searchProduct`,
    async (
        {
            page,
            take,
            isProductList,
            withReviewCount,
        }: { page: number; take: number; isProductList?: boolean; withReviewCount?: boolean },
        { getState, rejectWithValue },
    ) => {
        try {
            const url = getSearchUrl(
                (getState() as AppState).productSearch,
                page,
                take,
                isProductList,
                withReviewCount,
            );
            const res = await getProductList(url.toString());
            return {
                productPage: res.data,
                url: `/product/list${url.search}`,
            };
        } catch (err) {
            return rejectWithValue(err?.response?.data);
        }
    },
);

export const moreFetch = createAsyncThunk(
    `productSearch/moreFetch`,
    async (
        {
            page,
            take,
            isProductList,
            withReviewCount,
        }: { page: number; take: number; isProductList?: boolean; withReviewCount?: boolean },
        { getState, rejectWithValue },
    ) => {
        try {
            const url = getSearchUrl(
                (getState() as AppState).productSearch,
                page,
                take,
                isProductList,
                withReviewCount,
            );
            const res = await getProductList(url.toString());
            return {
                productPage: res.data,
                url: `/product/list${url.search}`,
            };
        } catch (err) {
            return rejectWithValue(err?.response?.data);
        }
    },
);

export const getCategoryList = createAsyncThunk(
    `productSearchSlice/getCategoryList`,
    async (_, { rejectWithValue, getState }) => {
        try {
            const { categoryList, isCategoryFetchEnd } = (getState() as AppState).productSearch;
            if (isCategoryFetchEnd) {
                return categoryList;
            }
            const res = await api.getCategoryList();
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const productSearchSlice = createSlice({
    name: 'productSearchSlice',
    initialState,
    reducers: {
        initProductSearchSlice: state => {
            return initialState;
        },
        initProductSearchCondition: state => {
            state.productType = initialState.productType;
            state.productName = initialState.productName;
            state.keyword = initialState.keyword;
            state.productOptionName = initialState.productOptionName;
            state.distributorName = initialState.distributorName;
            state.category = initialState.category;
            state.taxStatus = initialState.taxStatus;
            state.startDate = initialState.startDate;
            state.endDate = initialState.endDate;
            state.startUpdateDate = initialState.startUpdateDate;
            state.endUpdateDate = initialState.endUpdateDate;
            state.shipmentDateOptionType = initialState.shipmentDateOptionType;
            state.isSpecialSale = initialState.isSpecialSale;
            state.saleStatus = initialState.saleStatus;
            state.saleType = initialState.saleType;
            state.useGift = initialState.useGift;
            state.isVideo = initialState.isVideo;
            state.filter = initialState.filter;
        },
        updateProductType: (state, action: PayloadAction<number>) => {
            state.productType = action.payload;
        },
        setTake: (state, action: PayloadAction<number>) => {
            state.take = action.payload;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        onChangeProductName: (state, action: PayloadAction<string>) => {
            state.productName = action.payload;
        },
        onChangeKeyword: (state, action: PayloadAction<string>) => {
            state.keyword = action.payload;
        },
        onChangeDistributorName: (state, action: PayloadAction<string>) => {
            state.distributorName = action.payload;
        },
        onChangeCategory: (state, action: PayloadAction<number>) => {
            state.category = { ...state.category, id: action.payload };
        },
        setCategory: (state, action: PayloadAction<{ id: number; name: string }>) => {
            state.category = action.payload;
        },
        onChangeSaleType: (state, action: PayloadAction<string>) => {
            state.saleType = action.payload;
        },
        onChangeSaleStatus: (state, action: PayloadAction<string[]>) => {
            state.saleStatus = action.payload;
        },
        onChangeIsVideo: (state, action: PayloadAction<string>) => {
            state.isVideo = action.payload;
        },
        addProductsInSelectedProducts: (state, action: PayloadAction<ProductOld[]>) => {
            state.selectedProducts = action.payload;
        },
        addProductInSelectedProducts: (state, action: PayloadAction<ProductOld>) => {
            state.selectedProducts = [...state.selectedProducts, action.payload];
        },
        removeProductInSelectedProducts: (state, action: PayloadAction<ProductOld>) => {
            state.selectedProducts = state.selectedProducts.filter(item => item.id !== action.payload.id);
        },
        removeAllProductInSelectedProducts: state => {
            state.selectedProducts = [];
        },
        onClickCreateDateButton: (state, action: PayloadAction<SimpleDateSelectType>) => {
            state.selectedStartDateType = action.payload;

            const { startDate, endDate } = getStartEndDate(action.payload);
            state.startDate = action.payload === 'allDate' ? new Date().toString() : startDate.toString();
            state.endDate = action.payload === 'allDate' ? new Date().toString() : endDate.toString();
        },
        setStartDate: (state, action: PayloadAction<string>) => {
            state.startDate = action.payload.toString();
        },
        setEndDate: (state, action: PayloadAction<string>) => {
            state.endDate = action.payload.toString();
        },
        onClickUpdateDateButton: (state, action: PayloadAction<SimpleDateSelectType>) => {
            state.selectedUpdateDateType = action.payload;

            const { startDate, endDate } = getStartEndDate(action.payload);
            state.startUpdateDate = action.payload === 'allDate' ? new Date().toString() : startDate.toString();
            state.endUpdateDate = action.payload === 'allDate' ? new Date().toString() : endDate.toString();
        },
        setStartUpdateDate: (state, action: PayloadAction<string>) => {
            state.startUpdateDate = action.payload.toString();
            state.endUpdateDate = action.payload.toString();
        },
        setEndUpdateDate: (state, action: PayloadAction<string>) => {
            state.endUpdateDate = action.payload.toString();
        },
        resetStartDates: state => {
            state.selectedStartDateType = 'allDate';
            state.startDate = new Date().toString();
            state.endDate = new Date().toString();
        },
        resetUpdateDates: state => {
            state.selectedUpdateDateType = 'allDate';
            state.startUpdateDate = new Date().toString();
            state.endUpdateDate = new Date().toString();
        },
        setUpdateFilter: (state, action: PayloadAction<filterType>) => {
            state.filter = action.payload;
        },
        setUpdateTaxStatus: (state, action: PayloadAction<taxStatusType>) => {
            state.taxStatus = action.payload;
        },
        setUseGift: (state, action: PayloadAction<string>) => {
            state.useGift = action.payload;
        },
        setShipmentDateOptionType: (state, action: PayloadAction<string>) => {
            state.shipmentDateOptionType = action.payload;
        },
        setIsSpecialSale: (state, action: PayloadAction<string>) => {
            state.isSpecialSale = action.payload;
        },
        onChangeProductOptionName: (state, action: PayloadAction<string>) => {
            state.productOptionName = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(searchProduct.fulfilled, (state, { payload }) => {
            state.searchResult = payload.productPage.products;
            state.searchResultPage = payload.productPage.page;
        });
        builder.addCase(getCategoryList.fulfilled, (state, { payload }) => {
            state.categoryList = payload;
            state.isCategoryFetchEnd = true;
        });
        builder.addCase(moreFetch.fulfilled, (state, { payload }) => {
            const existingIds = new Set(state.searchResult.map(p => p.id));
            const newItems = payload.productPage.products.filter(p => !existingIds.has(p.id));
            state.searchResult.push(...newItems);
            state.searchResultPage = payload.productPage.page;
        });
    },
});

export default productSearchSlice.reducer;
export const {
    initProductSearchSlice,
    initProductSearchCondition,
    updateProductType,
    onChangeProductName,
    onChangeKeyword,
    onChangeCategory,
    onChangeDistributorName,
    onChangeSaleStatus,
    onChangeIsVideo,
    onChangeSaleType,
    addProductsInSelectedProducts,
    addProductInSelectedProducts,
    removeProductInSelectedProducts,
    removeAllProductInSelectedProducts,
    setPage,
    setTake,
    setCategory,
    onClickCreateDateButton,
    setStartDate,
    setEndDate,
    onClickUpdateDateButton,
    setEndUpdateDate,
    setStartUpdateDate,
    setUpdateFilter,
    setUpdateTaxStatus,
    setUseGift,
    onChangeProductOptionName,
    setShipmentDateOptionType,
    setIsSpecialSale,
    resetUpdateDates,
    resetStartDates,
} = productSearchSlice.actions;
